import { watch } from 'vue'

export function useEchoCalculations(formData) {
    // Calculate birth date from CNP
    const birthDateDinCNP = () => {
        const cnp = formData.cnpPacient.value.trim()
        if (cnp.length < 7) {
            return
        }

        const sex = parseInt(cnp.slice(0, 1), 10)
        let an = parseInt(cnp.slice(1, 3), 10)
        const luna = parseInt(cnp.slice(3, 5), 10)
        const zi = parseInt(cnp.slice(5, 7), 10)

        // Set sex (even numbers is female, odd numbers is male)
        if (sex % 2 === 1) {
            formData.sexPacient.value = 'M'
        } else {
            formData.sexPacient.value = 'F'
        }

        if (luna === 0 || zi === 0) {
            return
        }

        // Determine full year based on sex digit
        let fullYear
        if (sex === 1 || sex === 2) {
            fullYear = 1900 + an
        } else if (sex === 3 || sex === 4) {
            fullYear = 1800 + an
        } else if (sex === 5 || sex === 6) {
            fullYear = 2000 + an
        } else if (sex === 7 || sex === 8) {
            fullYear = 2000 + an
        } else if (sex === 9) {
            fullYear = 2000 + an
        }

        // Format as YYYY-MM-DD for input[type="date"]
        const birthDate = `${fullYear}-${String(luna).padStart(2, '0')}-${String(zi).padStart(2, '0')}`
        formData.birthDatePacient.value = birthDate
    }

    // Calculate left ventricular mass
    const calculareMasaVS = () => {
        const dtdvs = parseFloat(formData.DTDVS.value)
        const sivd = parseFloat(formData.SIVd.value)
        const ppvs = parseFloat(formData.PPVSd.value)
        const bsa = parseFloat(formData.bsaPacient.value)

        if (!bsa || !dtdvs || !sivd || !ppvs || dtdvs === 0) return

        const masaVS = (0.8 * (1.04 * (Math.pow(dtdvs + sivd + ppvs, 3) - Math.pow(dtdvs, 3))) + 0.6) / 1000
        const indexare = Math.round((masaVS / bsa) * 100) / 100
        formData.MasaVS.value = indexare.toFixed(2)
    }

    // Calculate RTW
    const calculareRTW = () => {
        const dtdvs = parseFloat(formData.DTDVS.value)
        const ppvs = parseFloat(formData.PPVSd.value)

        if (!dtdvs || !ppvs || dtdvs === 0) return

        const rtw = Math.round(((2 * ppvs) / dtdvs) * 100) / 100
        formData.RTW.value = rtw.toFixed(2)
    }

    // Calculate ejection fraction by volume
    const calculareFEVolProcent = () => {
        const vtdvs = parseFloat(formData.VTDVS.value)
        const vtsvs = parseFloat(formData.VTSVS.value)

        if (!vtdvs || !vtsvs || vtdvs - vtsvs <= 0) return

        const diff = vtdvs - vtsvs
        const proc = Math.round((diff / vtdvs) * 100)
        formData.FEvol.value = proc.toFixed(2)
    }

    // Add observation
    const addObservation = (obs) => {
        const plainText = obs.replace(/<[^>]*>/g, '')
        if (formData.concluzii.value) {
            formData.concluzii.value += plainText + '. '
        } else {
            formData.concluzii.value = plainText + '. '
        }
    }

    // Setup watchers
    // Computed BSA
    watch([formData.greutatePacient, formData.inaltimePacient], () => {
        if (formData.greutatePacient.value && formData.inaltimePacient.value) {
            const g = parseFloat(formData.greutatePacient.value)
            const h = parseFloat(formData.inaltimePacient.value)
            if (!isNaN(g) && !isNaN(h)) {
                formData.bsaPacient.value = (Math.round(0.016667 * Math.sqrt(g * h) * 100) / 100).toFixed(2)
            }
        }
    })

    // Watch CNP for birth date calculation
    watch(() => formData.cnpPacient.value, (newValue) => {
        if (newValue && newValue.trim().length >= 7) {
            birthDateDinCNP()
        }
    }, { immediate: true })

    // Index by BSA
    watch([formData.DTDVS, formData.bsaPacient], () => {
        if (formData.DTDVS.value && formData.bsaPacient.value) {
            const val = parseFloat(formData.DTDVS.value)
            const bsa = parseFloat(formData.bsaPacient.value)
            if (!isNaN(val) && !isNaN(bsa) && bsa > 0) {
                formData.DTDVSbsa.value = (val / bsa).toFixed(2)
            }
        }
    })

    watch([formData.DTSVS, formData.bsaPacient], () => {
        if (formData.DTSVS.value && formData.bsaPacient.value) {
            const val = parseFloat(formData.DTSVS.value)
            const bsa = parseFloat(formData.bsaPacient.value)
            if (!isNaN(val) && !isNaN(bsa) && bsa > 0) {
                formData.DTSVSbsa.value = (val / bsa).toFixed(2)
            }
        }
    })

    watch([formData.VTDVS, formData.bsaPacient], () => {
        if (formData.VTDVS.value && formData.bsaPacient.value) {
            const val = parseFloat(formData.VTDVS.value)
            const bsa = parseFloat(formData.bsaPacient.value)
            if (!isNaN(val) && !isNaN(bsa) && bsa > 0) {
                formData.VTDVSbsa.value = (val / bsa).toFixed(2)
            }
        }
    })

    watch([formData.VTSVS, formData.bsaPacient], () => {
        if (formData.VTSVS.value && formData.bsaPacient.value) {
            const val = parseFloat(formData.VTSVS.value)
            const bsa = parseFloat(formData.bsaPacient.value)
            if (!isNaN(val) && !isNaN(bsa) && bsa > 0) {
                formData.VTSVSbsa.value = (val / bsa).toFixed(2)
            }
        }
    })

    // Watch for calculations
    watch([formData.DTDVS, formData.SIVd, formData.PPVSd, formData.bsaPacient], () => {
        calculareMasaVS()
    })

    watch([formData.PPVSd, formData.DTDVS], () => {
        calculareRTW()
    })

    watch([formData.VTDVS, formData.VTSVS], () => {
        calculareFEVolProcent()
    })

    return {
        birthDateDinCNP,
        calculareMasaVS,
        calculareRTW,
        calculareFEVolProcent,
        addObservation
    }
}
